/**
 * Status-fane komponent
 */

import React from 'react';
import { RefreshCw, Info, Users, PlusCircle, Trophy, Sword, ChevronRight } from 'lucide-react';
import { TIME_FILTERS } from '../constants/config';

export const StatusTab = ({ 
  filteredKm, 
  timeFilter, 
  onTimeFilterChange,
  syncing,
  onSync,
  isAnonymous,
  competitions,
  userId,
  onCompetitionClick,
  onShowJoinModal,
  onShowCreateModal,
  leaderboard
}) => {
  console.log('StatusTab render:', { 
    syncing, 
    isAnonymous, 
    hasOnSync: typeof onSync === 'function',
    userId 
  });

  // Helper function to check if competition is finished
  const isCompetitionFinished = (comp) => {
    // Hvis allerede arkivert, er den ferdig
    if (comp.archived) return true;
    
    // Longest distance: Check if end date has passed
    if (comp.mode === 'longest_distance' && comp.endDate) {
      return new Date(comp.endDate) < new Date();
    }
    
    // Tug of war: Check if one team has reached the goal
    if (comp.mode === 'tug_of_war' && comp.goal && leaderboard) {
      const teamA = comp.participants.slice(0, Math.ceil(comp.participants.length / 2));
      const teamB = comp.participants.slice(Math.ceil(comp.participants.length / 2));
      
      const teamAKm = teamA.reduce((sum, id) => {
        const participant = leaderboard.find(l => l.id === id);
        return sum + (participant?.competitionKm?.[comp.id] || 0);
      }, 0);
      
      const teamBKm = teamB.reduce((sum, id) => {
        const participant = leaderboard.find(l => l.id === id);
        return sum + (participant?.competitionKm?.[comp.id] || 0);
      }, 0);
      
      const difference = Math.abs(teamAKm - teamBKm);
      return difference >= comp.goal;
    }
    
    // First to goal: Check if anyone has reached the goal
    if (comp.mode === 'first_to_goal' && comp.goal && leaderboard) {
      const hasWinner = comp.participants.some(id => {
        const participant = leaderboard.find(l => l.id === id);
        if (!participant) return false;
        const km = participant.competitionKm?.[comp.id] || 0;
        return km >= comp.goal;
      });
      return hasWinner;
    }
    
    return false;
  };

  return (
    <div className="space-y-6">
      {/* KM Card */}
      <section className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-orange-100/40 border border-orange-50 overflow-hidden relative">
        <div className="flex gap-2 mb-6">
          {Object.entries(TIME_FILTERS).map(([key, value]) => (
            <button
              key={value}
              onClick={() => onTimeFilterChange(value)}
              className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                timeFilter === value
                  ? 'bg-orange-600 text-white shadow-md shadow-orange-100'
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              {value === 'week' ? 'Siste 7 dager' : value === 'month' ? 'Måned' : 'År'}
            </button>
          ))}
        </div>

        <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.1em] mb-1 flex items-center gap-1">
          Løpte Kilometer <Info className="w-3 h-3 text-slate-300" />
        </p>

        <div className="flex justify-between items-end">
          <h2 className="text-6xl font-black text-slate-800 tabular-nums tracking-tighter">
            {filteredKm.toFixed(1)}
            <span className="text-xl font-medium text-slate-300 ml-2">km</span>
          </h2>

          <button
            onClick={() => {
              console.log('Sync button clicked!', { syncing, isAnonymous, onSync: typeof onSync });
              onSync();
            }}
            disabled={syncing || isAnonymous}
            className={`p-4 bg-orange-600 text-white rounded-2xl shadow-lg shadow-orange-200 active:scale-90 transition-all ${
              syncing ? 'animate-spin' : ''
            }`}
          >
            <RefreshCw className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={onShowJoinModal}
          className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col items-center gap-2 hover:border-orange-300 transition-all hover:shadow-md"
        >
          <Users className="text-orange-600 w-6 h-6" />
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Bli med
          </span>
        </button>

        <button
          onClick={onShowCreateModal}
          className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col items-center gap-2 hover:border-orange-300 transition-all hover:shadow-md"
        >
          <PlusCircle className="text-orange-600 w-6 h-6" />
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Opprett
          </span>
        </button>
      </div>

      {/* Active Competitions */}
      <section className="space-y-3">
        <h3 className="font-black text-[10px] uppercase tracking-[0.15em] text-slate-400 px-1">
          Aktive Konkurranser
        </h3>

        {competitions
          .filter(comp => comp.participants.includes(userId) && !isCompetitionFinished(comp))
          .map(comp => (
            <div
              key={comp.id}
              onClick={() => {
                onCompetitionClick(comp.id);
              }}
              className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center justify-between group cursor-pointer hover:border-orange-400 transition-all shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center shadow-inner">
                  {comp.mode === 'tug_of_war' ? (
                    <Sword className="text-red-500 w-5 h-5" />
                  ) : (
                    <Trophy className="text-orange-600 w-5 h-5" />
                  )}
                </div>
                <div>
                  <p className="font-black text-sm text-slate-800 tracking-tight">
                    {String(comp.title)}
                  </p>
                  <p className="text-[9px] font-black text-orange-400 uppercase tracking-tighter">
                    #{String(comp.code)}
                  </p>
                </div>
              </div>
              <ChevronRight className="text-slate-200 group-hover:text-orange-500 transition-colors" />
            </div>
          ))}
      </section>
    </div>
  );
};
